import { getProjectDescription, getProjectImages } from './legacyData';

export const PORTFOLIO_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  HIDDEN: 'hidden',
};

export const PORTFOLIO_MEDIA_SOURCE = {
  CLIENT: 'client',
  ARTISAN: 'artisan',
};

export const PORTFOLIO_PHASE = {
  NONE: 'none',
  BEFORE: 'before',
  AFTER: 'after',
  DURING: 'during',
  DETAIL: 'detail',
};

export const PORTFOLIO_PHASE_LABELS = {
  [PORTFOLIO_PHASE.NONE]: 'Non classée',
  [PORTFOLIO_PHASE.BEFORE]: 'Avant',
  [PORTFOLIO_PHASE.AFTER]: 'Après',
  [PORTFOLIO_PHASE.DURING]: 'Pendant les travaux',
  [PORTFOLIO_PHASE.DETAIL]: 'Détail',
};

export const PORTFOLIO_SOURCE_LABELS = {
  [PORTFOLIO_MEDIA_SOURCE.CLIENT]: 'Photo du client',
  [PORTFOLIO_MEDIA_SOURCE.ARTISAN]: 'Ajoutée par l’artisan',
};

export const getPortfolio = (project = {}) => project.portfolio || {};

export const getPortfolioStatus = (project = {}) => {
  const status = getPortfolio(project).status;
  return Object.values(PORTFOLIO_STATUS).includes(status) ? status : 'available';
};

const hashValue = (value = '') => {
  let hash = 0;
  const input = String(value);
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const readMediaUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    return String(
      value.url
      || value.downloadURL
      || value.downloadUrl
      || value.src
      || value.path
      || '',
    ).trim();
  }
  return '';
};

export const normalizePortfolioPhase = (value) => (
  Object.values(PORTFOLIO_PHASE).includes(value) ? value : PORTFOLIO_PHASE.NONE
);

export const makePortfolioMediaId = (source, url, suffix = '') => {
  const safeSource = source === PORTFOLIO_MEDIA_SOURCE.ARTISAN
    ? PORTFOLIO_MEDIA_SOURCE.ARTISAN
    : PORTFOLIO_MEDIA_SOURCE.CLIENT;
  return `${safeSource}-${hashValue(`${url}-${suffix}`)}`;
};

export const normalizePortfolioMediaItem = (value, fallbackSource = PORTFOLIO_MEDIA_SOURCE.CLIENT, index = 0) => {
  const url = readMediaUrl(value);
  if (!url) return null;

  const rawSource = typeof value === 'object' ? value.source : fallbackSource;
  const source = rawSource === PORTFOLIO_MEDIA_SOURCE.ARTISAN
    ? PORTFOLIO_MEDIA_SOURCE.ARTISAN
    : PORTFOLIO_MEDIA_SOURCE.CLIENT;
  const id = String(
    (typeof value === 'object' && value.id)
    || makePortfolioMediaId(source, url, index),
  ).trim();

  return {
    id,
    url,
    source,
    phase: normalizePortfolioPhase(typeof value === 'object' ? value.phase : PORTFOLIO_PHASE.NONE),
    storage_path: typeof value === 'object' ? String(value.storage_path || value.storagePath || '').trim() : '',
    original_name: typeof value === 'object' ? String(value.original_name || value.originalName || value.name || '').trim() : '',
    uploaded_at: typeof value === 'object' ? value.uploaded_at || value.uploadedAt || null : null,
  };
};

const uniqueMedia = (items = []) => {
  const seenUrls = new Set();
  return items.reduce((result, item, index) => {
    const normalized = normalizePortfolioMediaItem(item, item?.source, index);
    if (!normalized || seenUrls.has(normalized.url)) return result;
    seenUrls.add(normalized.url);
    result.push(normalized);
    return result;
  }, []);
};

export const getPortfolioMedia = (project = {}) => {
  const portfolio = getPortfolio(project);
  const storedMedia = Array.isArray(portfolio.media)
    ? portfolio.media
    : [];
  const legacyCustomMedia = Array.isArray(portfolio.custom_images)
    ? portfolio.custom_images
    : [];
  const stored = uniqueMedia([
    ...storedMedia,
    ...legacyCustomMedia.map((item) => ({
      ...(typeof item === 'object' ? item : { url: item }),
      source: PORTFOLIO_MEDIA_SOURCE.ARTISAN,
    })),
  ]);

  const storedByUrl = new Map(stored.map((item) => [item.url, item]));
  const clientMedia = getProjectImages(project).map((url, index) => {
    const existing = storedByUrl.get(url);
    return normalizePortfolioMediaItem({
      ...(existing || {}),
      id: existing?.id || makePortfolioMediaId(PORTFOLIO_MEDIA_SOURCE.CLIENT, url, index),
      url,
      source: PORTFOLIO_MEDIA_SOURCE.CLIENT,
    }, PORTFOLIO_MEDIA_SOURCE.CLIENT, index);
  }).filter(Boolean);

  const legacySelected = Array.isArray(portfolio.selected_images)
    ? portfolio.selected_images.filter(Boolean)
    : [];
  const knownClientUrls = new Set(clientMedia.map((item) => item.url));
  const additionalMedia = uniqueMedia([
    ...stored.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.ARTISAN),
    ...legacySelected
      .filter((url) => !knownClientUrls.has(readMediaUrl(url)))
      .map((url, index) => ({
        url: readMediaUrl(url),
        source: PORTFOLIO_MEDIA_SOURCE.ARTISAN,
        phase: PORTFOLIO_PHASE.NONE,
        id: makePortfolioMediaId(PORTFOLIO_MEDIA_SOURCE.ARTISAN, readMediaUrl(url), index),
      })),
  ]).filter((item) => !knownClientUrls.has(item.url));

  const media = uniqueMedia([...clientMedia, ...additionalMedia]);
  const coverUrl = readMediaUrl(portfolio.cover_image);

  if (!coverUrl) return media;
  return [
    ...media.filter((item) => item.url === coverUrl),
    ...media.filter((item) => item.url !== coverUrl),
  ];
};

export const getPortfolioImages = (project = {}) => getPortfolioMedia(project).map((item) => item.url);

export const getPortfolioBeforeAfter = (project = {}) => {
  const portfolio = getPortfolio(project);
  const media = getPortfolioMedia(project);
  const pair = portfolio.before_after || (
    Array.isArray(portfolio.before_after_pairs) ? portfolio.before_after_pairs[0] : null
  );

  if (!pair || pair.enabled === false) return null;

  const resolveMedia = (id, url, phase) => (
    media.find((item) => id && item.id === id)
    || media.find((item) => url && item.url === url)
    || media.find((item) => item.phase === phase)
    || null
  );

  const before = resolveMedia(pair.before_id, pair.before_url, PORTFOLIO_PHASE.BEFORE);
  const after = resolveMedia(pair.after_id, pair.after_url, PORTFOLIO_PHASE.AFTER);

  if (!before || !after || before.id === after.id) return null;

  return {
    enabled: true,
    title: String(pair.title || 'Avant / Après').trim(),
    before,
    after,
  };
};

export const getPortfolioTitle = (project = {}) => {
  const portfolio = getPortfolio(project);
  const description = getProjectDescription(project);
  const descriptionTitle = description ? description.split(/\n|[.!?]/)[0].trim().slice(0, 90) : '';
  return String(
    portfolio.title
      || project.demand_title
      || project.title
      || project.speciality_type
      || descriptionTitle
      || 'Projet réalisé',
  ).trim();
};

export const getPortfolioSummary = (project = {}) => {
  const portfolio = getPortfolio(project);
  return String(portfolio.summary || getProjectDescription(project) || '').trim();
};

export const getPortfolioCategory = (project = {}) => {
  const portfolio = getPortfolio(project);
  const labels = { window: 'Menuiserie', electricity: 'Électricité' };
  const source = portfolio.category || project.speciality_type || project.demand_type || 'Travaux';
  return String(labels[source] || source).trim();
};

export const isPortfolioPublished = (project = {}) => (
  getPortfolioStatus(project) === PORTFOLIO_STATUS.PUBLISHED
);

export const sortPortfolioProjects = (projects = []) => [...projects].sort((a, b) => {
  const featuredDelta = Number(Boolean(b?.portfolio?.featured)) - Number(Boolean(a?.portfolio?.featured));
  if (featuredDelta !== 0) return featuredDelta;

  const dateValue = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return dateValue(b?.portfolio?.published_at || b?.updated_at || b?.date)
    - dateValue(a?.portfolio?.published_at || a?.updated_at || a?.date);
});
